import * as React from 'react'
import { Button as MUIButton, Box, CircularProgress, SxProps, Theme } from '@mui/material'
import { default as cx } from 'classnames'
import { styles } from './styles/button'

interface ButtonProps {
  className?: string
  children?: React.ReactNode
  color?: 'default' | 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  disabled?: boolean
  icon?: React.ReactElement
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  loading?: boolean
  newFeatureDate?: Date
  size?: 'small' | 'medium' | 'large'
  text?: string
  variant?: 'text' | 'outlined' | 'contained'
  vertical?: boolean
  sx?: SxProps<Theme>
  type?: 'button' | 'submit' | 'reset'
  [key: string]: any
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    className,
    children,
    color = 'primary',
    disabled,
    icon,
    startIcon,
    endIcon,
    onClick,
    loading = false,
    newFeatureDate,
    size = 'medium',
    text,
    variant = 'contained',
    vertical = false,
    sx,
    ...other
  } = props

  let displayNewFeatureTag = false
  if (newFeatureDate) {
    const newFeatureDaysLimit = 60
    const currentDate = new Date()
    const timeDifference = currentDate.getTime() - newFeatureDate.getTime()
    const daysDifference = timeDifference / (1000 * 3600 * 24)
    if (daysDifference < newFeatureDaysLimit) displayNewFeatureTag = true
  }

  const allowedColors = ['inherit', 'primary', 'secondary', 'success', 'error', 'info', 'warning']

  // Wrap icon in Box for proper spacing (FontAwesome doesn't support sx prop)
  const wrappedIcon = icon ? (
    <Box
      component="span"
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: 'auto',
        height: '20px',
        ...(vertical ? {} : { marginRight: '6px' }),
      }}
    >
      {icon}
    </Box>
  ) : null

  // Wrap startIcon with spacing
  const wrappedStartIcon = startIcon ? (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>{startIcon}</Box>
  ) : null

  // Wrap endIcon with spacing
  const wrappedEndIcon = endIcon ? (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>{endIcon}</Box>
  ) : null

  return (
    <MUIButton
      ref={ref}
      className={className}
      color={allowedColors.includes(color) ? color as any : 'primary'}
      disabled={disabled || loading}
      onClick={onClick}
      size={size}
      variant={variant}
      sx={sx}
      {...other}
    >
      {wrappedStartIcon}
      {wrappedIcon}
      {children ?? text}
      {wrappedEndIcon}
      {displayNewFeatureTag && <Box sx={styles.newFeature}>NEW!</Box>}
      {loading && <CircularProgress sx={styles.buttonProgress} size={20} />}
    </MUIButton>
  )
})

Button.displayName = 'Button'

export default Button
